import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { useCampaignId } from "../hooks/useCampaignId";

type ContactType = { id: string; name: string; color: string | null };
type Contact = {
  id: string;
  campaign_id: string;
  type_id: string | null;
  full_name: string;
  org: string | null;
  title: string | null;
  email: string | null;
  phone: string | null;
  notes: string | null;
  voter_id: string | null;
  user_id: string | null;
  contact_types?: ContactType | null;
};

const card: React.CSSProperties = {
  background: "#fff",
  border: "1px solid #e5e7eb",
  borderRadius: 12,
  padding: 12,
  marginBottom: 12,
};
const input: React.CSSProperties = {
  padding: "10px 12px",
  borderRadius: 10,
  border: "1px solid #e5e7eb",
  width: "100%",
};
const btn: React.CSSProperties = {
  padding: "10px 12px",
  borderRadius: 10,
  border: "1px solid #e5e7eb",
  background: "#fff",
  cursor: "pointer",
  fontWeight: 700,
};
const primaryBtn: React.CSSProperties = {
  ...btn,
  border: "none",
  background: "#0B2E5B",
  color: "#fff",
};
const pill = (color?: string | null): React.CSSProperties => ({
  display: "inline-block",
  padding: "4px 8px",
  borderRadius: 999,
  fontSize: 12,
  background: color || "rgba(11,46,91,0.08)",
  color: "#fff",
});

function normalizeContactJoin(row: any): Contact {
  const ct = Array.isArray(row?.contact_types)
    ? row.contact_types[0] ?? null
    : row?.contact_types ?? null;
  return { ...row, contact_types: ct };
}

export default function ContactsPage() {
  const campaignId = useCampaignId();

  const [types, setTypes] = useState<ContactType[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>("");

  const [showEdit, setShowEdit] = useState(false);
  const [editing, setEditing] = useState<Contact | null>(null);

  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    if (!campaignId) return;
    setErr(null);
    setMsg(null);

    (async () => {
      // Ensure default types once (ignore error if they already exist)
      const { error: seedErr } = await supabase.rpc("ensure_default_contact_types", {
        p_campaign_id: campaignId,
      });
      if (seedErr) {
        // non-fatal; proceed to fetch anyway
      }

      const [tRes, cRes] = await Promise.all([
        supabase
          .from("contact_types")
          .select("id,name,color")
          .eq("campaign_id", campaignId)
          .order("name", { ascending: true }),
        supabase
          .from("contacts")
          .select(
            "id,campaign_id,type_id,full_name,org,title,email,phone,notes,voter_id,user_id,contact_types(id,name,color)"
          )
          .eq("campaign_id", campaignId)
          .order("full_name", { ascending: true }),
      ]);

      if (!tRes.error) setTypes((tRes.data ?? []) as ContactType[]);
      if (!cRes.error) {
        const normalized = ((cRes.data ?? []) as any[]).map(normalizeContactJoin) as Contact[];
        setContacts(normalized);
      }
    })();
  }, [campaignId]);

  function openCreate() {
    if (!campaignId) return;
    setEditing({
      id: "",
      campaign_id: campaignId,
      type_id: types[0]?.id ?? null,
      full_name: "",
      org: "",
      title: "",
      email: "",
      phone: "",
      notes: "",
      voter_id: null,
      user_id: null,
      contact_types: null,
    });
    setShowEdit(true);
  }
  function openEdit(c: Contact) {
    setEditing(c);
    setShowEdit(true);
  }
  function closeEdit() {
    setEditing(null);
    setShowEdit(false);
  }

  async function saveContact() {
    if (!editing || !campaignId || !editing.full_name.trim()) return;
    setErr(null);
    setMsg(null);

    const payload = {
      campaign_id: campaignId,
      type_id: editing.type_id,
      full_name: editing.full_name.trim(),
      org: editing.org || null,
      title: editing.title || null,
      email: editing.email || null,
      phone: editing.phone || null,
      notes: editing.notes || null,
      voter_id: editing.voter_id,
      user_id: editing.user_id,
    };

    if (editing.id) {
      const { error } = await supabase.from("contacts").update(payload).eq("id", editing.id);
      if (error) {
        setErr(error.message);
        return;
      }
      setMsg("Contact updated.");
    } else {
      const { error } = await supabase.from("contacts").insert(payload);
      if (error) {
        setErr(error.message);
        return;
      }
      setMsg("Contact added.");
    }

    const { data: cRaw } = await supabase
      .from("contacts")
      .select(
        "id,campaign_id,type_id,full_name,org,title,email,phone,notes,voter_id,user_id,contact_types(id,name,color)"
      )
      .eq("campaign_id", campaignId)
      .order("full_name", { ascending: true });

    setContacts(((cRaw ?? []) as any[]).map(normalizeContactJoin) as Contact[]);
    closeEdit();
  }

  return (
    <div style={{ padding: 16 }}>
      <div
        style={{
          ...card,
          display: "grid",
          gridTemplateColumns: "1fr auto",
          gap: 8,
          alignItems: "center",
        }}
      >
        <div>
          <div style={{ fontWeight: 700, fontSize: 18 }}>Contacts</div>
          <div style={{ color: "#6b7280" }}>
            Directory of donors, volunteers, election offices, vendors, and more.
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button style={primaryBtn} onClick={openCreate} disabled={!campaignId}>
            + Add Contact
          </button>
        </div>
      </div>

      <div
        style={{
          ...card,
          display: "grid",
          gridTemplateColumns: "2fr 1fr",
          gap: 8,
        }}
      >
        <input
          style={input}
          placeholder="Search name, org, email, phone…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <select
          style={input}
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
        >
          <option value="">All types</option>
          {types.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
      </div>

      {err && (
        <div
          style={{
            ...card,
            borderColor: "#fecaca",
            background: "#fef2f2",
            color: "#991B1B",
          }}
        >
          Error: {err}
        </div>
      )}
      {msg && (
        <div
          style={{
            ...card,
            borderColor: "#a7f3d0",
            background: "#ecfdf5",
            color: "#065f46",
          }}
        >
          {msg}
        </div>
      )}

      <div style={card}>
        {contacts.length === 0 && (
          <div style={{ color: "#6b7280" }}>No contacts yet.</div>
        )}
        {contacts
          .filter((c) => {
            const norm = (s?: string | null) => (s || "").toLowerCase();
            if (typeFilter && c.type_id !== typeFilter) return false;
            if (!q.trim()) return true;
            const needle = q.trim().toLowerCase();
            return [c.full_name, c.org, c.title, c.email, c.phone, c.notes, c.contact_types?.name]
              .some((v) => norm(v).includes(needle));
          })
          .map((c) => (
            <div
              key={c.id}
              style={{
                display: "grid",
                gridTemplateColumns: "1fr auto",
                gap: 8,
                padding: "10px 0",
                borderTop: "1px solid #f1f5f9",
              }}
            >
              <div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    flexWrap: "wrap",
                  }}
                >
                  <strong>{c.full_name}</strong>
                  {c.contact_types?.name && (
                    <span style={pill(c.contact_types?.color || undefined)}>
                      {c.contact_types?.name}
                    </span>
                  )}
                </div>
                <div style={{ color: "#6b7280", fontSize: 13 }}>
                  {[c.title, c.org].filter(Boolean).join(" @ ")}
                </div>
                <div style={{ color: "#6b7280", fontSize: 13 }}>
                  {[c.email, c.phone].filter(Boolean).join(" • ")}
                </div>
                {c.notes && <div style={{ marginTop: 6 }}>{c.notes}</div>}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button style={btn} onClick={() => openEdit(c)}>
                  Edit
                </button>
              </div>
            </div>
          ))}
      </div>

      {showEdit && editing && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.3)",
            display: "grid",
            placeItems: "center",
            padding: 16,
            zIndex: 50,
          }}
        >
          <div style={{ ...card, width: "100%", maxWidth: 640 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 8,
              }}
            >
              <div style={{ fontWeight: 700 }}>
                {editing.id ? "Edit contact" : "Add contact"}
              </div>
              <button style={btn} onClick={() => setShowEdit(false)}>
                Close
              </button>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              <div>
                <label style={{ fontSize: 13, color: "#374151" }}>Full name *</label>
                <input
                  style={input}
                  value={editing.full_name}
                  onChange={(e) =>
                    setEditing({ ...(editing as Contact), full_name: e.target.value })
                  }
                />
              </div>
              <div>
                <label style={{ fontSize: 13, color: "#374151" }}>Type</label>
                <select
                  style={input}
                  value={editing.type_id ?? ""}
                  onChange={(e) =>
                    setEditing({
                      ...(editing as Contact),
                      type_id: e.target.value || null,
                    })
                  }
                >
                  <option value="">—</option>
                  {types.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ fontSize: 13, color: "#374151" }}>Organization</label>
                <input
                  style={input}
                  value={editing.org || ""}
                  onChange={(e) =>
                    setEditing({ ...(editing as Contact), org: e.target.value })
                  }
                />
              </div>
              <div>
                <label style={{ fontSize: 13, color: "#374151" }}>Title</label>
                <input
                  style={input}
                  value={editing.title || ""}
                  onChange={(e) =>
                    setEditing({ ...(editing as Contact), title: e.target.value })
                  }
                />
              </div>

              <div>
                <label style={{ fontSize: 13, color: "#374151" }}>Email</label>
                <input
                  style={input}
                  value={editing.email || ""}
                  onChange={(e) =>
                    setEditing({ ...(editing as Contact), email: e.target.value })
                  }
                />
              </div>
              <div>
                <label style={{ fontSize: 13, color: "#374151" }}>Phone</label>
                <input
                  style={input}
                  value={editing.phone || ""}
                  onChange={(e) =>
                    setEditing({ ...(editing as Contact), phone: e.target.value })
                  }
                />
              </div>

              <div style={{ gridColumn: "1 / -1" }}>
                <label style={{ fontSize: 13, color: "#374151" }}>Notes</label>
                <textarea
                  style={input}
                  rows={3}
                  value={editing.notes || ""}
                  onChange={(e) =>
                    setEditing({ ...(editing as Contact), notes: e.target.value })
                  }
                />
              </div>

              <div>
                <label style={{ fontSize: 13, color: "#374151" }}>
                  Link to voter_id (optional)
                </label>
                <input
                  style={input}
                  value={editing.voter_id || ""}
                  onChange={(e) =>
                    setEditing({
                      ...(editing as Contact),
                      voter_id: e.target.value || null,
                    })
                  }
                  placeholder="Paste an ID for now"
                />
              </div>
              <div>
                <label style={{ fontSize: 13, color: "#374151" }}>
                  Link to user_id (optional)
                </label>
                <input
                  style={input}
                  value={editing.user_id || ""}
                  onChange={(e) =>
                    setEditing({
                      ...(editing as Contact),
                      user_id: e.target.value || null,
                    })
                  }
                  placeholder="Paste an ID for now"
                />
              </div>
            </div>

            <div
              style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}
            >
              <button style={btn} onClick={closeEdit}>
                Cancel
              </button>
              <button
                style={primaryBtn}
                onClick={saveContact}
                disabled={!editing.full_name.trim()}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
